"use client";

import { useState } from "react";
import Image from "next/image";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { Box } from "@mui/material";
import DrawerMenu from "@/src/components/main-site/_partials/header/drawer-menu/drawer-menu";
import ThemeToggle from "@/src/components/main-site/_partials/header/theme-toggle/theme-toggle";
import DesktopMenu from "@/src/components/main-site/_partials/header/desktop-menu/desktop-menu";
import SignInButton from "@/src/components/main-site/_partials/header/sign-in-button/sign-in-button";
import MenuItem from "@/src/_types/menu-item";
import { mapToMenuItems } from "@/src/utils/database-helpers/menu-helper";
import {
  actionSx,
  doubleRule,
  FONT_TEXT,
  SHEET_GUTTER,
  SHEET_MAX_WIDTH,
} from "@/src/components/common/broadsheet/tokens";
import { normalizeSitePath } from "@/src/utils/paths";

const isFrontPage = (pathname: string | null) =>
  normalizeSitePath(pathname) === "/";

const MainSiteHeader = ({
  journalAbbr,
  mainJournal,
  journal,
}: {
  journalAbbr: string;
  mainJournal: any;
  journal: any;
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pathname = usePathname();
  const front = isFrontPage(pathname);

  const menuItems: MenuItem[] = mainJournal.menu
    ? mapToMenuItems(mainJournal.menu, mainJournal)
    : [];
  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  const url = journal.slug;
  const homeHref = journal.is_main_site ? "/" : `/journals/${journal.path}`;

  const logo = (
    <Box
      component={NextLink}
      href={homeHref}
      aria-label={`${mainJournal.title ?? "Brieflands"} home`}
      sx={{ display: "inline-block", lineHeight: 0, flex: "none" }}
    >
      <Image
        src="/logo.svg"
        alt={mainJournal.title ?? "Brieflands"}
        width={front ? 225 : 154}
        height={front ? 44 : 30}
        priority
        style={{
          display: "block",
          width: front ? "225px" : "154px",
          maxWidth: `min(100%, ${front ? 60 : 45}vw)`,
          height: "auto",
        }}
      />
    </Box>
  );

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: "custom.newsprint",
        color: "custom.ink",
      }}
    >
      <Box
        sx={{
          maxWidth: SHEET_MAX_WIDTH,
          mx: "auto",
          px: SHEET_GUTTER,
          pt: { xs: 0.5, sm: 1.5 },
          ...doubleRule,
        }}
      >
        {front ? (
          <Box sx={{ textAlign: "center", pt: { xs: 0.75, sm: 0.5 } }}>
            <Box sx={{ display: "inline-block" }}>{logo}</Box>
            {journal.tagline ? (
              <Box
                component="p"
                sx={{
                  m: 0,
                  mt: 0.375,
                  fontFamily: FONT_TEXT,
                  fontStyle: "italic",
                  fontSize: { xs: "0.8125rem", md: "0.9375rem" },
                  lineHeight: 1.4,
                  color: "custom.inkMuted",
                }}
              >
                {journal.tagline}
              </Box>
            ) : null}
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            minHeight: "2.75rem",
            pt: front ? 0.25 : { xs: 0.25, sm: 0 },
            pb: 0.375,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              minWidth: 0,
              flex: front ? "1 1 0" : "0 1 auto",
            }}
          >
            <Box
              component="button"
              type="button"
              aria-label="Open menu"
              onClick={() => toggleDrawer(true)}
              sx={{
                ...actionSx("outline", "small"),
                display: { xs: "inline-flex", lg: "none" },
                cursor: "pointer",
              }}
            >
              Menu
            </Box>
            {front ? null : logo}
          </Box>

          <Box
            component="nav"
            aria-label="Main"
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "stretch",
              justifyContent: "center",
              flex: front ? "0 1 auto" : 1,
              minWidth: 0,
            }}
          >
            <DesktopMenu menuItems={menuItems} />
          </Box>

          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              justifyContent: "flex-end",
              flex: front ? "1 1 0" : "none",
            }}
          >
            {!journal.is_main_site && url && <SignInButton url={url} />}
            <ThemeToggle />
          </Box>
        </Box>
      </Box>

      <DrawerMenu
        open={drawerOpen}
        toggleDrawer={toggleDrawer}
        menuItems={menuItems}
        mainJournal={mainJournal}
        mainSite={journal.is_main_site}
        supportUrl={journal.meta_data?.support_url ?? "#"}
        signInUrl={url}
      />
    </Box>
  );
};

export default MainSiteHeader;
